import React, { forwardRef } from 'react';
import { clsx } from 'clsx';
import Select from './Select';
import { PhoneIcon } from '@heroicons/react/24/outline';

// Popular country codes - simple and reliable
const COUNTRY_CODES = [
  { value: '+961', label: 'LB +961 (Lebanon)', country: 'Lebanon' },
  { value: '+1', label: 'US +1 (United States)', country: 'United States' },
  { value: '+1', label: 'CA +1 (Canada)', country: 'Canada' },
  { value: '+44', label: 'GB +44 (United Kingdom)', country: 'United Kingdom' },
  { value: '+33', label: 'FR +33 (France)', country: 'France' },
  { value: '+49', label: 'DE +49 (Germany)', country: 'Germany' },
  { value: '+39', label: 'IT +39 (Italy)', country: 'Italy' },
  { value: '+34', label: 'ES +34 (Spain)', country: 'Spain' },
  { value: '+31', label: 'NL +31 (Netherlands)', country: 'Netherlands' },
  { value: '+41', label: 'CH +41 (Switzerland)', country: 'Switzerland' },
  { value: '+43', label: 'AT +43 (Austria)', country: 'Austria' },
  { value: '+32', label: 'BE +32 (Belgium)', country: 'Belgium' },
  { value: '+45', label: 'DK +45 (Denmark)', country: 'Denmark' },
  { value: '+46', label: 'SE +46 (Sweden)', country: 'Sweden' },
  { value: '+47', label: 'NO +47 (Norway)', country: 'Norway' },
  { value: '+358', label: 'FI +358 (Finland)', country: 'Finland' },
  { value: '+353', label: 'IE +353 (Ireland)', country: 'Ireland' },
  { value: '+351', label: 'PT +351 (Portugal)', country: 'Portugal' },
  { value: '+30', label: 'GR +30 (Greece)', country: 'Greece' },
  { value: '+48', label: 'PL +48 (Poland)', country: 'Poland' },
  { value: '+420', label: 'CZ +420 (Czech Republic)', country: 'Czech Republic' },
  { value: '+36', label: 'HU +36 (Hungary)', country: 'Hungary' },
  { value: '+40', label: 'RO +40 (Romania)', country: 'Romania' },
  { value: '+385', label: 'HR +385 (Croatia)', country: 'Croatia' },
  { value: '+386', label: 'SI +386 (Slovenia)', country: 'Slovenia' },
  { value: '+421', label: 'SK +421 (Slovakia)', country: 'Slovakia' },
  { value: '+372', label: 'EE +372 (Estonia)', country: 'Estonia' },
  { value: '+371', label: 'LV +371 (Latvia)', country: 'Latvia' },
  { value: '+370', label: 'LT +370 (Lithuania)', country: 'Lithuania' },
  { value: '+7', label: 'RU +7 (Russia)', country: 'Russia' },
  { value: '+91', label: 'IN +91 (India)', country: 'India' },
  { value: '+86', label: 'CN +86 (China)', country: 'China' },
  { value: '+81', label: 'JP +81 (Japan)', country: 'Japan' },
  { value: '+82', label: 'KR +82 (South Korea)', country: 'South Korea' },
  { value: '+65', label: 'SG +65 (Singapore)', country: 'Singapore' },
  { value: '+60', label: 'MY +60 (Malaysia)', country: 'Malaysia' },
  { value: '+66', label: 'TH +66 (Thailand)', country: 'Thailand' },
  { value: '+84', label: 'VN +84 (Vietnam)', country: 'Vietnam' },
  { value: '+63', label: 'PH +63 (Philippines)', country: 'Philippines' },
  { value: '+62', label: 'ID +62 (Indonesia)', country: 'Indonesia' },
  { value: '+61', label: 'AU +61 (Australia)', country: 'Australia' },
  { value: '+64', label: 'NZ +64 (New Zealand)', country: 'New Zealand' },
  { value: '+52', label: 'MX +52 (Mexico)', country: 'Mexico' },
  { value: '+55', label: 'BR +55 (Brazil)', country: 'Brazil' },
  { value: '+54', label: 'AR +54 (Argentina)', country: 'Argentina' },
  { value: '+56', label: 'CL +56 (Chile)', country: 'Chile' },
  { value: '+57', label: 'CO +57 (Colombia)', country: 'Colombia' },
  { value: '+51', label: 'PE +51 (Peru)', country: 'Peru' },
  { value: '+58', label: 'VE +58 (Venezuela)', country: 'Venezuela' },
  { value: '+27', label: 'ZA +27 (South Africa)', country: 'South Africa' },
  { value: '+20', label: 'EG +20 (Egypt)', country: 'Egypt' },
  { value: '+234', label: 'NG +234 (Nigeria)', country: 'Nigeria' },
  { value: '+254', label: 'KE +254 (Kenya)', country: 'Kenya' },
  { value: '+971', label: 'AE +971 (UAE)', country: 'UAE' },
  { value: '+966', label: 'SA +966 (Saudi Arabia)', country: 'Saudi Arabia' },
  { value: '+974', label: 'QA +974 (Qatar)', country: 'Qatar' },
  { value: '+965', label: 'KW +965 (Kuwait)', country: 'Kuwait' },
  { value: '+973', label: 'BH +973 (Bahrain)', country: 'Bahrain' },
  { value: '+968', label: 'OM +968 (Oman)', country: 'Oman' },
  { value: '+972', label: 'IL +972 (Israel)', country: 'Israel' },
  { value: '+90', label: 'TR +90 (Turkey)', country: 'Turkey' }
];

interface PhoneInputProps {
  label?: string;
  error?: string;
  helperText?: string;
  size?: 'sm' | 'md' | 'lg';
  disabled?: boolean;
  required?: boolean;
  className?: string;
  value?: string; // Combined phone number like "+1 555-123-4567"
  onChange?: (value: string) => void;
  placeholder?: string;
  name?: string;
}

const PhoneInput = forwardRef<HTMLInputElement, PhoneInputProps>(
  ({ 
    label,
    error,
    helperText,
    size = 'md',
    disabled = false,
    required = false,
    className,
    value = '',
    onChange,
    placeholder = 'Enter phone number',
    name,
    ...props
  }, ref) => {
    // Parse the combined phone value into country code and phone number
    const parsePhoneValue = (phoneValue: string) => {
      if (!phoneValue) return { countryCode: '+1', phoneNumber: '' };
      
      // Find the longest matching country code
      const sortedCodes = COUNTRY_CODES
        .map(c => c.value)
        .sort((a, b) => b.length - a.length); // Sort by length descending
      
      for (const code of sortedCodes) {
        if (phoneValue.startsWith(code)) {
          const phoneNumber = phoneValue.slice(code.length).trim();
          return { countryCode: code, phoneNumber };
        }
      }
      
      // Default fallback
      return { countryCode: '+1', phoneNumber: phoneValue };
    };

    const { countryCode, phoneNumber } = parsePhoneValue(value);

    const handleCountryChange = (newCountryCode: string) => {
      const combinedValue = phoneNumber ? `${newCountryCode} ${phoneNumber}` : newCountryCode;
      onChange?.(combinedValue);
    };

    const handlePhoneChange = (newPhoneNumber: string) => {
      const combinedValue = newPhoneNumber ? `${countryCode} ${newPhoneNumber}` : countryCode;
      onChange?.(combinedValue);
    };


    const sizeClasses = {
      sm: 'px-3 py-1.5 text-sm',
      md: 'px-3 py-2 text-sm',
      lg: 'px-4 py-3 text-base'
    };

    const baseClasses = 'block w-full rounded-r-lg border-l-0 border transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed';
    
    const stateClasses = error
      ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
      : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500';

    return (
      <div className={clsx('w-full', className)}>
        {label && (
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {label}
            {required && <span className="text-red-500 ml-1">*</span>}
          </label>
        )}
        
        <div className="flex">
          {/* Country Code Dropdown */}
          <div className="w-32 flex-shrink-0">
            <Select
              options={COUNTRY_CODES}
              value={countryCode}
              onChange={(value) => handleCountryChange(value as string)}
              size={size}
              disabled={disabled}
              showSearch
              className="rounded-r-none border-r-0"
            />
          </div>
          
          {/* Phone Number Input */}
          <div className="flex-1 relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <PhoneIcon className="h-4 w-4 text-gray-400" />
            </div>
            
            <input
              {...props}
              ref={ref}
              type="tel"
              name={name}
              className={clsx(
                baseClasses,
                stateClasses,
                sizeClasses[size],
                'pl-10'
              )}
              disabled={disabled}
              required={required}
              value={phoneNumber}
              onChange={(e) => handlePhoneChange(e.target.value)}
              placeholder={placeholder}
            />
          </div>
          
          {/* Hidden input for forms that query DOM by name */}
          {name && (
            <input
              type="hidden"
              name={name}
              value={value}
              readOnly
            />
          )}
        </div>
        
        {error && (
          <p className="mt-1 text-sm text-red-600">{error}</p>
        )}
        
        {helperText && !error && (
          <p className="mt-1 text-sm text-gray-500">{helperText}</p>
        )}
      </div>
    );
  }
);

PhoneInput.displayName = 'PhoneInput';

export default PhoneInput; 